import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { Tooltip } from "@components/ui/Tooltip"
import { ticketModals } from "@features/ferve/tickets-modal"
import { useFerveEmbed } from "@features/ferve/useFerveEmbed"
import { trackFilmInterest } from "@features/tracking/film-history"
import { getMixpanelPayload, trackEvent } from "@features/tracking/tracking"
import { WishlistButton } from "@features/wishlist/WishlistButton"
import { SessionTicketsFragment } from "@generated-types"
import { useBreakpoint } from "@hooks/useBreakpoint"
import { Accessibilities } from "@utils/accessibilities"
import { formatNiceDate, formatNiceTime } from "@utils/date-format"
import { tv } from "@utils/tw"
import { motion } from "motion/react"
import { useRef } from "react"
import { EventBadges } from "./EventBadges"
import { FadeScroller } from "@components/atoms/FadeScroller"
import { isPastEvent } from "@utils/past-event"
import { useFilmBadges } from "@features/program/useFilmBadges"

export type Props = {
  session: SessionTicketsFragment
  alwaysOpenTicketsInModal?: boolean
}

export function SessionTicketWidget(props: Props) {
  const info = props.session.info
  const styles = createStyles()

  const isMobile = useBreakpoint().match(/BASE|sm/)

  const openAsModal = props.alwaysOpenTicketsInModal || isMobile
  const embed = useFerveEmbed({ itemHash: info?.hash! })
  const hasInfo = info?.venue?.titleSimple || !!info?.access?.filter((a) => a && !!Accessibilities[a]).length
  const embedContainer = useRef<HTMLDivElement>(null)

  const longVenueName = [info?.venue?.titleSimple, info?.venue?.titleSub].filter(Boolean).join(" - ")
  const shortVenueName = [info?.venue?.titleShort || info?.venue?.titleSimple, info?.venue?.titleSub]
    .filter(Boolean)
    .join(" - ")

  const badges = useFilmBadges(props)

  let isPast = badges.some((b) => b.type === "past-event")
  let isSoldOut = badges.some((b) => b.type === "sold-out")

  const guests = (props.session.guests?.map((g) => g?.title).filter(Boolean) as string[]) ?? []

  return (
    <div data-mixpanel-area="Ticketing Widget">
      <div className={styles.base()}>
        <div className={styles.card()}>
          <div className={styles.info()}>
            <div className="flex gap-2 items-center type-ui-s @md:type-ui-l">
              {formatNiceDate(props.session.info?.dateTime!)}
              {", "}
              {formatNiceTime(props.session.info?.dateTime!)}
            </div>
            {hasInfo && (
              <FadeScroller overscroll="auto" className="w-full">
                <div className="flex gap-2 items-center md:flex-wrap">
                  <EventBadges session={props.session} shape="rounded" />
                  {longVenueName && (
                    <Chip variant="high" shape="rounded" className="hidden md:inline-flex">
                      {longVenueName}
                    </Chip>
                  )}
                  {shortVenueName && (
                    <Chip variant="high" shape="rounded" className="md:hidden">
                      {shortVenueName}
                    </Chip>
                  )}
                  {guests.length > 0 && (
                    <Tooltip label={joinWithAnd(guests)}>
                      <Chip variant="high" shape="rounded">
                        {guests.length} Guest{guests.length > 1 ? "s" : ""}
                      </Chip>
                    </Tooltip>
                  )}
                  {props.session.info?.access?.map((access) => <AccessIcon key={access} access={access!} />)}
                </div>
              </FadeScroller>
            )}
          </div>
          <div className={styles.buttons()}>
            <WishlistButton
              film={props.session.info?.ferveId!}
              className="px-3 max-sm:w-control md:px-4 [&_[data-part=label]]:hidden @md:sm:[&_[data-part=label]]:block first:part-[icon]:ml-0 @md:first:part-[icon]:-ml-2"
            />
            <Button
              shape="branded"
              href={props.session.info?.externalTicketUrl}
              target="_blank"
              variant="primary"
              className={"disabled:!opacity-30"}
              disabled={isPast || isSoldOut ? true : false}
              data-book-button
              onClick={(e) => {
                if (props.session.info?.externalTicketUrl) return
                if (openAsModal) {
                  ticketModals.push({
                    type: "embed",
                    itemHash: info?.hash!,
                  })
                  return
                }
                if (embed.isOpen) {
                  embed.close()
                } else {
                  embed.open()
                  trackEvent(
                    "Open Ferve Embed",
                    {
                      OpenMode: openAsModal ? "Drawer" : "Inline",
                    },
                    e.currentTarget,
                  )
                }
                trackFilmInterest(getMixpanelPayload().FilmUri, "cart")
              }}
              loading={embed.isLoading}
            >
              Book
            </Button>
          </div>
        </div>
        <motion.div
          variants={{ open: { height: "auto" }, closed: { height: 0 } }}
          initial="closed"
          animate={embed.isReady ? "open" : "closed"}
          ref={embedContainer}
          className={styles.embed()}
          style={{ height: 0 }}
          transition={{ duration: 0.5 }}
        >
          {embed.child}
        </motion.div>
      </div>
    </div>
  )
}
const createStyles = tv({
  slots: {
    base: ["bg-bg-low text-fg rounded-film overflow-hidden @container"],
    card: ["px-4 py-4 @md:px-4 @md:py-3 gap-3 md:gap-3 flex items-center"],
    info: ["flex flex-1 flex-col gap-2 pl-2 min-w-0"],
    buttons: "flex flex-none gap-2",
    embed: "bg-bg border-[2px] -mt-[4px] border-bg-low rounded-bl-film rounded-br-film",
  },
})

const AccessIcon = ({ access }: { access: string }) => {
  if (Accessibilities[access]) {
    return (
      <Tooltip label={access}>
        <img
          src={Accessibilities[access]}
          alt={access}
          title={access}
          className="flex w-[0.9em] h-[0.9em] object-contain"
        />
      </Tooltip>
    )
  }
  return null
}

function joinWithAnd(arr: string[]) {
  if (arr.length === 1) return arr[0]
  if (arr.length === 2) return arr.join(" & ")
  return arr.slice(0, -1).join(", ") + ", & " + arr[arr.length - 1]
}
